import { useParams } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { DEVICES } from "@/lib/devices";
import { toast } from "sonner";

export default function Activate() {
  useSeo({ title: "Activate your tester account", noindex: true });
  const { token } = useParams();
  const { data, isLoading } = useQuery({ queryKey: ["activate", token], queryFn: () => api.activateGet(token) });
  const [form, setForm] = useState({ name: "", city: "", phone: "", upi: "", terms_accepted: false });
  const [devices, setDevices] = useState([{ platform: "android", form: "phone", brand: "Xiaomi", model: "Redmi 12", os_version: "13", tier: "T0" }]);
  const [ownedSlugs, setOwnedSlugs] = useState([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (data?.tester) {
      setForm((f) => ({ ...f, name: data.tester.name || "", city: data.tester.city || "", upi: data.tester.upi || "" }));
      if (data.tester.devices?.length) setDevices(data.tester.devices);
      if (data.tester.owned_device_slugs?.length) setOwnedSlugs(data.tester.owned_device_slugs);
    }
  }, [data]);

  function toggleOwned(slug) {
    setOwnedSlugs((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));
  }

  if (isLoading) return <div className="app-main"><div className="wrap"><p>Loading…</p></div></div>;
  if (!data) return <div className="app-main"><div className="wrap"><h1>Link expired</h1></div></div>;

  function addPhone() { setDevices([...devices, { platform: "android", form: "phone", brand: "Samsung", model: "", os_version: "14", tier: "T0" }]); }
  function addLaptop() { setDevices([...devices, { platform: "web", form: "laptop", os: "windows", browser: "chrome", browser_version: "128", tier: "T0" }]); }
  function updateDevice(i, patch) { setDevices(devices.map((d, j) => (j === i ? { ...d, ...patch } : d))); }
  function removeDevice(i) { setDevices(devices.filter((_, j) => j !== i)); }

  async function activate() {
    if (!/^[\w.\-]{2,}@[a-z]{2,}$/i.test(form.upi)) { toast.error("Enter a valid UPI ID like name@okaxis"); return; }
    if (!form.terms_accepted) { toast.error("Please accept the tester terms"); return; }
    try {
      await api.activatePost(token, { ...form, devices, owned_device_slugs: ownedSlugs });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  if (done) return (
    <div className="app-main"><div className="wrap" style={{ maxWidth: 640, textAlign: "center", padding: "40px 0" }} data-testid="activate-done">
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--green)", color: "#fff", display: "grid", placeItems: "center", fontSize: 28, margin: "0 auto 18px", fontWeight: 800 }}>✓</div>
      <h1>You're active.</h1>
      <p className="lede" style={{ margin: "12px auto 24px" }}>Session offers will come by email. First to accept gets the session.</p>
    </div></div>
  );

  return (
    <>
      <header className="site-header"><div className="wrap"><a className="logo" href="/"><i></i>99tests</a></div></header>
      <main className="app-main"><div className="wrap" style={{ maxWidth: 640 }}>
        <span className="pill neutral">Takes 2 minutes</span>
        <h1 style={{ fontSize: "clamp(30px,6vw,44px)", marginTop: 12 }}>Welcome back. Start getting paid sessions.</h1>
        <p className="lede">Confirm a few details and you'll receive session offers by email — ₹300 per 45-minute session, plus bonuses, paid to your UPI.</p>
        <div className="panel-soft" style={{ padding: 26, marginTop: 24 }}>
          <div className="row">
            <div className="field"><label>Name</label><input data-testid="activate-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="field"><label>City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          </div>
          <div className="field"><label>Your phones and laptops</label>
            {devices.map((d, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8, padding: 12, border: "1px solid var(--rule)", borderRadius: "var(--r-m)", background: "#fff" }}>
                <select value={d.platform} onChange={(e) => updateDevice(i, { platform: e.target.value })}><option value="android">Android</option><option value="ios">iOS</option><option value="web">Laptop/Web</option></select>
                <input placeholder={d.platform === "web" ? "OS e.g. windows" : "Model e.g. Redmi 12"} value={d.platform === "web" ? (d.os || "") : (d.model || "")} onChange={(e) => updateDevice(i, d.platform === "web" ? { os: e.target.value } : { model: e.target.value })} />
                <button className="btn ghost sm" onClick={() => removeDevice(i)}>×</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn ghost sm" data-testid="add-phone-btn" onClick={addPhone}>Add a phone</button>
              <button type="button" className="btn ghost sm" data-testid="add-laptop-btn" onClick={addLaptop}>Add a laptop</button>
            </div>
          </div>
          <div className="field"><label>Which of these phones do you own? <span className="hint" style={{ fontWeight: 400 }}>(helps us match you to jobs faster)</span></label>
            <div data-testid="owned-devices" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
              {DEVICES.map((dv) => {
                const on = ownedSlugs.includes(dv.slug);
                return (
                  <button type="button" key={dv.slug} data-testid={`owned-${dv.slug}`} onClick={() => toggleOwned(dv.slug)}
                    style={{ cursor: "pointer", padding: "7px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600,
                      border: `1.5px solid ${on ? "var(--green)" : "var(--rule-strong)"}`,
                      background: on ? "rgba(14,124,75,.1)" : "#fff", color: on ? "var(--green)" : "var(--ink)" }}>
                    {on ? "✓ " : ""}{dv.model}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="field"><label>UPI ID for payouts</label>
            <input data-testid="activate-upi" value={form.upi} onChange={(e) => setForm({ ...form, upi: e.target.value })} placeholder="name@bank" />
            <p className="hint">Fees and bonuses are paid here after each job.</p></div>
          <div className="field"><label className="checkline"><input type="checkbox" data-testid="activate-terms" checked={form.terms_accepted} onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })} /><span>I agree to the tester terms: keep builds confidential, use only my own devices, report honestly.</span></label></div>
          <button className="btn green block" data-testid="activate-submit" onClick={activate}>Activate and start receiving offers</button>
        </div>
      </div></main>
    </>
  );
}
